import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Plus, Trash2, Edit3, LayoutGrid, ArrowUp, ArrowDown, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APP_COLORS = ['#4f46e5','#0891b2','#059669','#d97706','#dc2626','#7c3aed','#db2777','#475569'];

function AppLauncherTab() {
    const { getAuthHeader } = useAuth();
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [editIndex, setEditIndex] = useState(null);
    const [form, setForm] = useState({ name: '', url: '', icon_url: '', color: APP_COLORS[0] });

    const fetchLinks = async () => {
        try {
            const res = await axios.get(`${API}/organizations/current`, { headers: getAuthHeader() });
            setLinks(res.data?.settings?.app_links || []);
        } catch { toast.error('Failed to load app links'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchLinks(); }, []);

    const saveLinks = async (newLinks) => {
        setSaving(true);
        try {
            await axios.put(`${API}/organizations/current`, {
                settings: { app_links: newLinks },
            }, { headers: getAuthHeader() });
            setLinks(newLinks);
            toast.success('App links saved');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const openAdd = () => {
        setEditIndex(null);
        setForm({ name: '', url: '', icon_url: '', color: APP_COLORS[0] });
        setShowDialog(true);
    };

    const openEdit = (idx) => {
        setEditIndex(idx);
        setForm({ ...links[idx] });
        setShowDialog(true);
    };

    const handleSave = () => {
        if (!form.name.trim() || !form.url.trim()) { toast.error('Name and URL are required'); return; }
        let url = form.url.trim();
        if (!/^https?:\/\//i.test(url)) url = 'https://' + url;
        const entry = {
            id: editIndex !== null ? links[editIndex].id : crypto.randomUUID(),
            name: form.name.trim(),
            url,
            icon_url: form.icon_url.trim() || '',
            color: form.color || APP_COLORS[0],
            order: editIndex !== null ? links[editIndex].order : links.length,
        };
        const newLinks = editIndex !== null
            ? links.map((l, i) => i === editIndex ? entry : l)
            : [...links, entry];
        saveLinks(newLinks);
        setShowDialog(false);
    };

    const handleDelete = (idx) => {
        const newLinks = links.filter((_, i) => i !== idx).map((l, i) => ({ ...l, order: i }));
        saveLinks(newLinks);
    };

    const moveLink = (idx, dir) => {
        const target = idx + dir;
        if (target < 0 || target >= links.length) return;
        const newLinks = [...links];
        [newLinks[idx], newLinks[target]] = [newLinks[target], newLinks[idx]];
        saveLinks(newLinks.map((l, i) => ({ ...l, order: i })));
    };

    const pickColor = (name, color) => {
        if (color) return color;
        let hash = 0;
        for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
        return APP_COLORS[Math.abs(hash) % APP_COLORS.length];
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>App Launcher</h3>
                    <p className="text-sm text-stone-500">Configure quick-access links that appear in the waffle menu for all team members.</p>
                </div>
                <Button onClick={openAdd} size="sm"><Plus className="w-4 h-4 mr-1" /> Add App</Button>
            </div>

            {links.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-stone-400 border border-dashed border-stone-300 rounded-xl">
                    <LayoutGrid className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm font-medium text-stone-500">No apps configured</p>
                    <p className="text-xs mt-1">Add external tools and websites your team uses daily.</p>
                </div>
            ) : (
                <div className="border border-stone-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 text-stone-500 text-xs">
                            <tr>
                                <th className="text-left px-4 py-2.5 font-medium">App</th>
                                <th className="text-left px-4 py-2.5 font-medium">URL</th>
                                <th className="text-right px-4 py-2.5 font-medium w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {links.map((link, idx) => (
                                <tr key={link.id || idx} className="hover:bg-stone-50">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            {link.icon_url ? (
                                                <img src={link.icon_url} alt="" className="w-8 h-8 rounded-lg object-cover bg-stone-100"
                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                            ) : null}
                                            <div
                                                className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                                                style={{
                                                    backgroundColor: pickColor(link.name, link.color),
                                                    display: link.icon_url ? 'none' : 'flex',
                                                }}
                                            >
                                                {(link.name || '?').charAt(0).toUpperCase()}
                                            </div>
                                            <span className="font-medium text-stone-900">{link.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-stone-500 hover:text-blue-600 truncate block max-w-xs">
                                            {link.url}
                                        </a>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => moveLink(idx, -1)} disabled={idx === 0}
                                                className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700 disabled:opacity-30">
                                                <ArrowUp className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => moveLink(idx, 1)} disabled={idx === links.length - 1}
                                                className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700 disabled:opacity-30">
                                                <ArrowDown className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => openEdit(idx)}
                                                className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700">
                                                <Edit3 className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => handleDelete(idx)}
                                                className="p-1 rounded hover:bg-red-100 text-stone-400 hover:text-red-600">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Live Preview */}
            {links.length > 0 && (
                <div>
                    <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Preview</h4>
                    <div className="inline-flex bg-white border border-stone-200 rounded-xl p-3 shadow-sm">
                        <div className="grid grid-cols-3 gap-1">
                            {links.map((link) => (
                                <div key={link.id || link.name} className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg w-20">
                                    {link.icon_url ? (
                                        <img src={link.icon_url} alt={link.name} className="w-10 h-10 rounded-xl object-cover bg-stone-100" />
                                    ) : (
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-base"
                                            style={{ backgroundColor: pickColor(link.name, link.color) }}>
                                            {(link.name || '?').charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <span className="text-[11px] text-stone-600 font-medium text-center leading-tight truncate w-full">{link.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editIndex !== null ? 'Edit App' : 'Add App'}</DialogTitle>
                        <DialogDescription>Configure an external app link for the launcher menu.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Name *</Label>
                            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Slack" />
                        </div>
                        <div>
                            <Label>URL *</Label>
                            <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} placeholder="e.g. https://slack.com" />
                        </div>
                        <div>
                            <Label>Icon URL (optional)</Label>
                            <Input value={form.icon_url} onChange={e => setForm(f => ({ ...f, icon_url: e.target.value }))} placeholder="e.g. https://example.com/icon.png" />
                            <p className="text-[11px] text-stone-400 mt-1">Paste a favicon or logo URL. Leave blank for a letter icon.</p>
                        </div>
                        <div>
                            <Label>Color</Label>
                            <div className="flex items-center gap-2 mt-1">
                                {APP_COLORS.map(c => (
                                    <button
                                        key={c}
                                        onClick={() => setForm(f => ({ ...f, color: c }))}
                                        className={`w-7 h-7 rounded-lg transition-all ${form.color === c ? 'ring-2 ring-offset-2 ring-stone-900 scale-110' : 'hover:scale-105'}`}
                                        style={{ backgroundColor: c }}
                                    />
                                ))}
                            </div>
                            <p className="text-[11px] text-stone-400 mt-1">Used as fallback when no icon URL is set.</p>
                        </div>
                        {/* Preview */}
                        <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                            {form.icon_url ? (
                                <img src={form.icon_url} alt="" className="w-10 h-10 rounded-xl object-cover bg-stone-100" />
                            ) : (
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-base"
                                    style={{ backgroundColor: form.color || APP_COLORS[0] }}>
                                    {(form.name || '?').charAt(0).toUpperCase()}
                                </div>
                            )}
                            <div>
                                <p className="text-sm font-medium text-stone-900">{form.name || 'App Name'}</p>
                                <p className="text-xs text-stone-400">{form.url || 'https://...'}</p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : (editIndex !== null ? 'Update' : 'Add App')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function AdminAppLauncherPage() {
    return (
        <AdminLayout title="App Launcher" description="Configure quick-access app links for your team">
            <AppLauncherTab />
        </AdminLayout>
    );
}
